import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { coachThreadSummaryQuery } from '@/lib/threadSummaryQueries';

export interface CoachThread {
  id: string;
  client_id: string;
  title: string | null;
  status: 'new' | 'active' | 'resolved';
  source_path: string | null;
  created_at: string;
  updated_at: string;
  client_last_read_at: string | null;
  coach_last_read_at?: string | null;
  client_name?: string;
  last_message_text?: string | null;
  last_message_at?: string | null;
  last_message_from_client?: boolean;
  needs_reply?: boolean;
}

export interface CoachMessage {
  id: string;
  thread_id: string;
  sender_role: 'client' | 'coach';
  sender_id: string;
  message_text: string;
  voice_url: string | null;
  button_label: string | null;
  button_route: string | null;
  button_type: string | null;
  created_at: string;
  reply_to_id?: string | null;
  edited_at?: string | null;
  reply_to?: {
    id: string;
    sender_role: 'client' | 'coach';
    message_text: string;
    voice_url: string | null;
  } | null;
}

export interface CoachClient {
  id: string;
  first_name: string;
  full_name: string;
}

export function useCoachThreads() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<CoachThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasUnread, setHasUnread] = useState(false);

  const fetchThreads = async () => {
    if (!user) return;

    const { data: threadData } = await coachThreadSummaryQuery(supabase);

    if (!threadData) {
      setLoading(false);
      return;
    }

    const clientIds = [...new Set(threadData.map(t => t.client_id))];
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, first_name, full_name')
      .in('id', clientIds);

    const nameMap = new Map<string, string>();
    (profiles || []).forEach(p => {
      const parts = (p.full_name || '').trim().split(/\s+/).filter(Boolean);
      const first = p.first_name || parts[0] || 'Client';
      const lastInitial = parts.length > 1 ? parts[parts.length - 1][0]?.toUpperCase() : '';
      nameMap.set(p.id, lastInitial ? `${first} ${lastInitial}.` : first);
    });

    const enriched: CoachThread[] = threadData.map(t => {
      // PostgREST applies the per-thread limit and both tables' existing RLS.
      const last = t.latest_messages?.[0] ?? null;
      const coachRead = (t as { coach_last_read_at?: string | null }).coach_last_read_at ?? null;
      const fromClient = last?.sender_role === 'client';
      const needsReply =
        !!last && fromClient && (!coachRead || new Date(last.created_at) > new Date(coachRead));
      return {
        id: t.id,
        client_id: t.client_id,
        title: t.title,
        status: t.status as CoachThread['status'],
        source_path: t.source_path,
        created_at: t.created_at,
        updated_at: t.updated_at,
        client_last_read_at: t.client_last_read_at ?? null,
        coach_last_read_at: coachRead,
        client_name: nameMap.get(t.client_id) || 'Client',
        last_message_text: last?.message_text ?? null,
        last_message_at: last?.created_at ?? null,
        last_message_from_client: fromClient,
        needs_reply: needsReply,
      };
    });

    // Most recent activity first
    enriched.sort((a, b) =>
      new Date(b.last_message_at || b.updated_at).getTime() -
      new Date(a.last_message_at || a.updated_at).getTime()
    );

    setThreads(enriched);
    setHasUnread(enriched.some(t => t.needs_reply));
    setLoading(false);
  };

  useEffect(() => {
    fetchThreads();

    if (!user) return;

    const channel = supabase
      .channel(`coach-threads-watch-${user.id}-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_threads',
      }, () => {
        fetchThreads();
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'client_messages',
      }, () => {
        fetchThreads();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markThreadRead = async (threadId: string) => {
    const now = new Date().toISOString();
    setThreads(prev => prev.map(t => (t.id === threadId ? { ...t, coach_last_read_at: now, needs_reply: false } : t)));
    setHasUnread(prev => prev && threads.some(t => t.id !== threadId && t.needs_reply));
    await supabase
      .from('client_threads')
      .update({ coach_last_read_at: now })
      .eq('id', threadId);
  };

  /** One conversation per client: return the existing thread or create it. */
  const getOrCreateThread = async (clientId: string): Promise<CoachThread | null> => {
    const existing = threads.find(t => t.client_id === clientId);
    if (existing) return existing;

    const { data: found } = await supabase
      .from('client_threads')
      .select('*')
      .eq('client_id', clientId)
      .limit(1);

    let row = found?.[0];
    if (!row) {
      const { data: created, error } = await supabase
        .from('client_threads')
        .insert({ client_id: clientId, status: 'active', title: 'Conversation' })
        .select()
        .single();
      if (error || !created) return null;
      row = created;
    }

    await fetchThreads();
    return {
      id: row.id,
      client_id: row.client_id,
      title: row.title,
      status: row.status as CoachThread['status'],
      source_path: row.source_path,
      created_at: row.created_at,
      updated_at: row.updated_at,
      client_last_read_at: row.client_last_read_at ?? null,
    };
  };

  return { threads, loading, hasUnread, refetch: fetchThreads, markThreadRead, getOrCreateThread };
}

export function useCoachThreadMessages(threadId: string | null) {
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMessages = async () => {
    if (!threadId) return;
    setLoading(true);
    const { data } = await supabase
      .from('client_messages')
      .select('*, reply_to:reply_to_id(id, sender_role, message_text, voice_url)')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data as unknown as CoachMessage[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMessages();

    if (!threadId) return;

    const channel = supabase
      .channel(`coach-thread-messages-${threadId}-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_messages',
        filter: `thread_id=eq.${threadId}`,
      }, () => {
        fetchMessages();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId]);

  return { messages, loading, refetch: fetchMessages };
}

export function useCoachClientList() {
  const { user } = useAuth();
  const [clients, setClients] = useState<CoachClient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetch = async () => {
      const { data: assignments } = await supabase
        .from('coach_clients')
        .select('client_id')
        .eq('coach_id', user.id);

      if (!assignments || assignments.length === 0) {
        setClients([]);
        setLoading(false);
        return;
      }

      const clientIds = assignments.map(a => a.client_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, full_name')
        .in('id', clientIds)
        .order('first_name', { ascending: true });

      setClients(
        (profiles || []).map(p => {
          const parts = (p.full_name || '').trim().split(/\s+/).filter(Boolean);
          const first = p.first_name || parts[0] || 'Client';
          const lastInitial = parts.length > 1 ? parts[parts.length - 1][0]?.toUpperCase() : '';
          return {
            id: p.id,
            first_name: lastInitial ? `${first} ${lastInitial}.` : first,
            full_name: p.full_name || 'Client',
          };
        })
      );
      setLoading(false);
    };

    fetch();
  }, [user]);

  return { clients, loading };
}
